/**
 * EstimateBuilder
 * ===============
 * A single-page, mobile-first, inline-editable roofing estimate that REPLACES
 * the old 10-step wizard. Every field that drives the PDF is editable inline;
 * the live HTML preview on the right mirrors what the PDF will look like.
 *
 * Design priorities (in order):
 *   1. Speed of edit — no separate "review" screen. What you see IS the quote.
 *   2. Mobile-first — roofers are usually on phones. Stacks to one column at <lg.
 *   3. Industry-standard scope-of-work defaults — pre-checked items match what
 *      AccuLynx, JobNimbus, and Roofr produce by default.
 *   4. "Magic" auto-fill via AI Vision + Pricing Profile, with a one-tap
 *      "Reset to AI defaults" per section so a misclick doesn't lose minutes.
 *   5. Customer-facing data (no contractor margin leak) — see Privacy audit.
 *
 * Data model unchanged from the wizard era; only the UI shape differs.
 * PATCH /api/quotes/{id} accepts the same fields.
 */
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowLeft, Download, Loader2, Plus, Trash2,
    Sparkles, ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw, MapPin,
    CheckCircle2, AlertTriangle, XCircle, Layers, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AIDisclaimerBanner } from '@/components/AIDisclaimerBanner';
import { RoofMapView } from '@/components/RoofMapView';
// NOTE: EagleView panel is intentionally hidden in the UI for now — the
// vendor requires a paid monthly contract minimum that we'll revisit later.
// All backend service files + EagleViewPanel.jsx are preserved so re-enabling
// is a 1-line import + JSX restore.
// import { EagleViewPanel } from '@/components/EagleViewPanel';
import api from '@/lib/api';
import { downloadAuthedFile } from '@/lib/download';
import { PdfPreviewPane } from '@/components/PdfPreviewPane';
import { buildAutofillFromAI } from '@/lib/autofill';

const $ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const $2 = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Industry-standard scope-of-work items found in nearly every residential
// roofing quote (AccuLynx, JobNimbus, Roofr, GAF QuickMeasure templates).
// Pre-checked items are typical defaults; the contractor toggles off what
// doesn't apply.
const SCOPE_DEFAULTS = [
    { key: 'tearoff',       label: 'Tear-off & disposal of existing roof',        checked: true,  hint: 'Remove old shingles, underlayment, & flashing' },
    { key: 'underlayment',  label: 'Synthetic underlayment installation',          checked: true,  hint: 'Industry standard — replaces felt paper' },
    { key: 'ice_water',     label: 'Ice & water shield (eaves + valleys)',         checked: true,  hint: 'Required in most northern states by code' },
    { key: 'drip_edge',     label: 'New drip edge along eaves + rakes',            checked: true,  hint: '' },
    { key: 'starter_strip', label: 'Starter strip course',                         checked: true,  hint: '' },
    { key: 'shingles',      label: 'New asphalt shingles installed per spec',      checked: true,  hint: '' },
    { key: 'ridge_cap',     label: 'Hip & ridge cap installation',                 checked: true,  hint: '' },
    { key: 'flashing',      label: 'New pipe boots & vent flashing',               checked: true,  hint: 'Includes step/counter flashing at walls + chimney' },
    { key: 'ridge_vent',    label: 'Continuous ridge vent (if applicable)',        checked: false, hint: 'Adds passive attic ventilation' },
    { key: 'decking',       label: 'Decking replacement as needed',                checked: false, hint: 'Billed per sheet — see Costs section' },
    { key: 'permit',        label: 'Pull building permit',                         checked: false, hint: 'Required in most jurisdictions; varies by city' },
    { key: 'haul_off',      label: 'Haul-off & dump fee',                          checked: true,  hint: '' },
    { key: 'cleanup',       label: 'Magnetic nail sweep + property cleanup',       checked: true,  hint: '' },
];


// ─────────────────────────────────────────────────────────────────────────────
// Collapsible section wrapper — used for every block on the estimate page.
// ─────────────────────────────────────────────────────────────────────────────
function Section({ title, subtitle, defaultOpen = true, action, children, testId }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card className="border-slate-200" data-testid={testId}>
            <div
                className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 cursor-pointer select-none hover:bg-slate-50"
                onClick={() => setOpen(o => !o)}
                role="button"
                aria-expanded={open}
                data-testid={testId ? `${testId}-toggle` : undefined}
            >
                <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-semibold text-slate-900">{title}</div>
                    {subtitle && <div className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {action}
                    {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
            </div>
            {open && <CardContent className="p-4">{children}</CardContent>}
        </Card>
    );
}


// Tiny labelled-input helper. Keeps the JSX readable without sacrificing the
// inline-edit feel.
function Field({ label, value, onChange, type = 'text', placeholder, suffix, testId, className, disabled }) {
    return (
        <div className={className}>
            {label && <Label className="text-xs text-slate-600">{label}</Label>}
            <div className="relative">
                <Input
                    type={type}
                    value={value ?? ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    data-testid={testId}
                    className={suffix ? 'pr-12' : ''}
                />
                {suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{suffix}</span>
                )}
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// The main page
// ─────────────────────────────────────────────────────────────────────────────
export default function EstimateBuilder() {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [pricingProfile, setPricingProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [estimating, setEstimating] = useState(false);
    const [mobilePreview, setMobilePreview] = useState(false);
    const [mapDialog, setMapDialog] = useState(false);
    const dirtyRef = useRef(false);
    const saveTimerRef = useRef(null);
    const roofMapRef = useRef(null);

    // ── Initial load ─────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        try {
            const [r, profRes] = await Promise.all([
                api.get(`/quotes/${quoteId}`),
                api.get('/me/pricing-profile').catch(() => ({ data: null })),
            ]);
            setQuote(r.data);
            if (profRes?.data?.profile) setPricingProfile(profRes.data.profile);
        } catch (e) {
            toast.error('Quote not found');
        } finally {
            setLoading(false);
        }
    }, [quoteId]);
    useEffect(() => { load(); }, [load]);

    // ── Auto-save with 800ms debounce. Every inline edit schedules a save;
    //    rapid edits coalesce. Manual Save button still works for impatient users.
    const scheduleSave = useCallback((partial) => {
        dirtyRef.current = true;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            if (!dirtyRef.current) return;
            setSaving(true);
            try {
                const r = await api.patch(`/quotes/${quoteId}`, partial);
                setQuote(prev => ({ ...prev, ...r.data }));
                dirtyRef.current = false;
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'Auto-save failed');
            } finally {
                setSaving(false);
            }
        }, 800);
    }, [quoteId]);

    const updateField = (key, value) => {
        setQuote(q => ({ ...q, [key]: value }));
        scheduleSave({ [key]: value });
    };
    const updateNested = (key, patch) => {
        setQuote(q => {
            const next = { ...(q?.[key] || {}), ...patch };
            scheduleSave({ [key]: next });
            return { ...q, [key]: next };
        });
    };

    // ── Manual save (used by Sign action) ────────────────────────────────────
    const flushSave = async () => {
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
        if (!dirtyRef.current) return;
        setSaving(true);
        try {
            await api.patch(`/quotes/${quoteId}`, {
                homeowner_name: quote.homeowner_name,
                property_address: quote.property_address,
                stories: Number(quote.stories || 1),
                scope_of_work: quote.scope_of_work,
                materials: quote.materials,
                costs: quote.costs,
                warranties: quote.warranties,
                payment_terms: quote.payment_terms,
                timeline: quote.timeline,
                custom_sections: quote.custom_sections || [],
                notes: quote.notes,
            });
            dirtyRef.current = false;
        } catch (e) {
            toast.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    // ── Run AI Vision ────────────────────────────────────────────────────────
    // One-click magic: run AI Vision, AND auto-apply the resulting sqft+material
    // to the materials list + costs in the same call. Used to be two clicks
    // ("Run AI Estimate" then "Auto-fill costs"); now it's one. The intelligent
    // part: materials list swaps based on AI material_guess (asphalt vs metal
    // vs tile) so a metal roof never gets GAF Timberline pre-filled by mistake.
    const runAIEstimate = async () => {
        if (!roofMapRef.current) { toast.error('Map not ready — re-open the map'); return; }
        const state = roofMapRef.current.getMapState();
        if (!state) { toast.error('Center the map on the house first'); return; }
        setEstimating(true);
        try {
            const r = await api.post(`/quotes/${quoteId}/roof/estimate`, {
                lat: state.center[1],
                lng: state.center[0],
                zoom: state.zoom,
                address: quote.property_address,
                year_built: quote?.year_built || undefined,
                sqft: quote?.living_sqft || undefined,
            }, { timeout: 60000 });

            // Build & save the materials + costs patch immediately based on AI output.
            const patch = buildAutofillFromAI({
                measurements: r.data?.measurements || r.data?.estimate,
                pricingProfile,
                stories: Number(quote?.stories || 1),
            });
            if (patch.materials && patch.costs) {
                await api.patch(`/quotes/${quoteId}`, {
                    materials: patch.materials,
                    costs: { ...(quote.costs || {}), ...patch.costs },
                });
            }
            await load();  // refresh local state with everything in one shot

            const conf = r.data?.estimate?.confidence || r.data?.demo_quality?.final_confidence || 'low';
            const mat = (r.data?.estimate?.material_guess || 'unknown').replace(/_/g, ' ');
            const sqft = r.data?.measurements?.roof_sqft;
            toast.success(
                sqft
                    ? `\u2728 ${Math.round(sqft).toLocaleString()} sqft · ${mat} \u00B7 ${conf} confidence \u2014 costs filled`
                    : `AI estimate complete \u2014 ${conf} confidence`
            );
            setMapDialog(false);  // close the map dialog so user lands on the filled quote
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'AI estimate failed');
        } finally {
            setEstimating(false);
        }
    };

    // ── Manual "Auto-fill from current measurements" — kept for users who ran
    //    AI in an older session and want to re-apply costs without re-running AI.
    const applyAIToCosts = () => {
        const m = quote?.roof?.measurements;
        if (!m?.roof_sqft) { toast.error('Run AI estimate first'); return; }
        const patch = buildAutofillFromAI({
            measurements: m,
            pricingProfile,
            stories: Number(quote?.stories || 1),
        });
        if (!patch.materials || !patch.costs) {
            toast.error('Couldn\u2019t auto-fill — check that AI measurements are present');
            return;
        }
        const nextCosts = { ...(quote.costs || {}), ...patch.costs };
        setQuote(q => ({ ...q, materials: patch.materials, costs: nextCosts }));
        scheduleSave({ materials: patch.materials, costs: nextCosts });
        const tmpl = patch._summary.template.replace(/_/g, ' ');
        toast.success(`Filled ${patch._summary.adjSquares} sq of ${tmpl} \u00B7 ${patch._summary.marginPct}% margin`);
    };

    // ── Derived costs ────────────────────────────────────────────────────────
    const c = quote?.costs || {};
    const subtotal = useMemo(() =>
        ['materials', 'labor', 'disposal', 'permit', 'other'].reduce((s, k) => s + Number(c[k] || 0), 0),
        [c],
    );
    const taxRate = Number(c.tax_rate || 0);
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;
    const rangeLowPct = Number(pricingProfile?.range_low_pct ?? quote?.range_estimate?.low_pct ?? 0.95);
    const rangeHighPct = Number(pricingProfile?.range_high_pct ?? quote?.range_estimate?.high_pct ?? 1.10);
    const rangeLow = total * rangeLowPct;
    const rangeHigh = total * rangeHighPct;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading estimate&hellip;
            </div>
        );
    }
    if (!quote) return <div className="text-slate-500">Not found</div>;

    const isLocked = quote.status === 'signed';
    const status = quote.status || 'draft';

    return (
        <div className="space-y-4 pb-24 lg:pb-6">
            {/* ── Sticky top bar ──────────────────────────────────────────── */}
            <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/85 backdrop-blur border-b border-slate-200">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <Button asChild variant="ghost" size="sm" data-testid="estimate-back-button" className="shrink-0">
                            <Link to="/app/quotes"><ArrowLeft className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Back</span></Link>
                        </Button>
                        <div className="min-w-0">
                            <div className="font-display font-semibold text-slate-900 truncate text-sm sm:text-base" data-testid="estimate-title">
                                Estimate {quote.quote_number}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{quote.property_address || 'No address'}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className="text-[10px] sm:text-xs capitalize hidden sm:inline-flex">{status}</Badge>
                        {saving && (
                            <span className="text-[10px] sm:text-xs text-slate-500 hidden md:flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" /> Saving&hellip;
                            </span>
                        )}
                        <Button
                            onClick={async () => {
                                await flushSave();
                                downloadAuthedFile(`/quotes/${quoteId}/pdf`, `${quote.quote_number || quoteId}.pdf`);
                            }}
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950"
                            data-testid="estimate-pdf-button"
                        >
                            <Download className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Download PDF</span>
                        </Button>
                    </div>
                </div>
            </div>

            <AIDisclaimerBanner />

            {/* ── Two-column layout (desktop) / single (mobile) ─────────── */}
            <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)] gap-4 lg:gap-6">
                {/* LEFT: editable estimate */}
                <div className="space-y-3">

                    {/* Customer + Property */}
                    <Section title="Customer & property" subtitle="Who and where" testId="section-customer">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field
                                label="Homeowner name"
                                value={quote.homeowner_name}
                                onChange={(v) => updateField('homeowner_name', v)}
                                placeholder="Jane Smith"
                                testId="field-homeowner-name"
                                disabled={isLocked}
                            />
                            <Field
                                label="Property address"
                                value={quote.property_address}
                                onChange={(v) => updateField('property_address', v)}
                                placeholder="123 Main St, City, ST 00000"
                                className="sm:col-span-2"
                                testId="field-property-address"
                                disabled={isLocked}
                            />
                            <div>
                                <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                                    Stories
                                    {quote.stories_source === 'public_records' && (
                                        <Badge variant="outline" className="h-4 text-[9px] font-normal px-1.5 py-0 border-emerald-200 bg-emerald-50 text-emerald-700">
                                            from public records
                                        </Badge>
                                    )}
                                </Label>
                                <Select
                                    value={String(quote.stories || 1)}
                                    onValueChange={(v) => updateField('stories', Number(v))}
                                    disabled={isLocked}
                                >
                                    <SelectTrigger data-testid="field-stories"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">1 story</SelectItem>
                                        <SelectItem value="2">2 stories (+15% labor)</SelectItem>
                                        <SelectItem value="3">3+ stories (+30% labor)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                            QuotesFlow generates a PDF you can download and hand-deliver, text, or share with the homeowner. We don&apos;t send marketing or sales emails on your behalf.
                        </p>
                    </Section>

                    {/* Roof measurements */}
                    <Section
                        title="Roof measurements"
                        subtitle={
                            quote?.roof?.measurements?.roof_sqft
                                ? `${Math.round(quote.roof.measurements.roof_sqft).toLocaleString()} sqft · ${quote.roof.measurements.pitch || '—'} pitch · ${(quote.roof.measurements.material_guess || 'unknown').replace(/_/g, ' ')}`
                                : 'Not measured yet — open the map to run AI Vision'
                        }
                        testId="section-roof"
                        action={
                            <Button
                                onClick={() => setMapDialog(true)}
                                size="sm"
                                variant="outline"
                                disabled={isLocked}
                                data-testid="estimate-open-map"
                            >
                                <MapPin className="h-3.5 w-3.5 mr-1" /> Open map
                            </Button>
                        }
                    >
                        {quote?.roof?.measurements ? (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <StatBox label="Sqft" value={quote.roof.measurements.roof_sqft ? `${Math.round(quote.roof.measurements.roof_sqft).toLocaleString()}` : '—'} />
                                    <StatBox label="Material" value={(quote.roof.measurements.material_guess || 'unknown').replace(/_/g, ' ')} />
                                    <StatBox label="Pitch" value={quote.roof.measurements.pitch || '—'} />
                                    <StatBox label="Complexity" value={quote.roof.measurements.complexity || '—'} />
                                </div>
                                {/* Material order math — surfaces the waste factor so the
                                    contractor (and reviewing homeowner) always know how many
                                    squares were *ordered* vs *measured*. Industry standard
                                    practice but rarely shown transparently in software. */}
                                {quote.roof.measurements.roof_sqft && (() => {
                                    const sqft = Number(quote.roof.measurements.roof_sqft);
                                    const wastePct = Number(pricingProfile?.waste_pct ?? 12);
                                    const measuredSq = sqft / 100;
                                    const orderedSq = measuredSq * (1 + wastePct / 100);
                                    return (
                                        <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[11px] text-slate-600 flex items-center justify-between gap-2 flex-wrap" data-testid="waste-factor-explainer">
                                            <span>
                                                Roof area <b className="text-slate-900 tabular-nums">{measuredSq.toFixed(1)} sq</b>
                                                <span className="mx-1 text-slate-400">×</span>
                                                <b className="text-slate-900">{wastePct}% waste</b>
                                                <span className="mx-1 text-slate-400">=</span>
                                                <b className="text-slate-900 tabular-nums">{orderedSq.toFixed(1)} sq</b> ordered
                                            </span>
                                            <span className="text-slate-400 text-[10px]">Industry standard 10-15%</span>
                                        </div>
                                    );
                                })()}
                                {/* Compact pre-flight pill — green=safe / yellow/red = expanded */}
                                {quote.roof.measurements.demo_quality && (
                                    <CompactPreflight quality={quote.roof.measurements.demo_quality} />
                                )}
                                <div className="flex flex-wrap gap-2">
                                    <Button onClick={applyAIToCosts} size="sm" variant="outline" disabled={isLocked} data-testid="apply-ai-costs">
                                        <Sparkles className="h-3.5 w-3.5 mr-1" /> Re-fill costs from current measurements
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500">
                                No measurements yet. <button onClick={() => setMapDialog(true)} className="underline font-medium text-slate-900">Open the map</button>, then click <b>Run AI Estimate</b> to auto-fill sqft, pitch, and material.
                            </div>
                        )}
                    </Section>

                    {/* EagleView premium upsell — INTENTIONALLY HIDDEN until
                        the vendor contract is in place (paid monthly minimum).
                        The full scaffold (backend + EagleViewPanel.jsx) is
                        preserved; restore by uncommenting the import above and
                        the JSX block below.
                        {quote?.roof?.measurements?.roof_sqft && (
                            <EagleViewPanel
                                quoteId={quoteId}
                                quote={quote}
                                disabled={isLocked}
                                onMeasurementsApplied={(m) => {
                                    const refined = {
                                        ...(quote.roof?.measurements || {}),
                                        roof_sqft: m.total_roof_area_sqft || quote.roof?.measurements?.roof_sqft,
                                        pitch: m.predominant_pitch || quote.roof?.measurements?.pitch,
                                        complexity: m.complexity_score || quote.roof?.measurements?.complexity,
                                        eagleview_source: true,
                                        eagleview_facets: m.facets_count,
                                        eagleview_ridge_ft: m.ridge_length_ft,
                                        eagleview_valley_ft: m.valley_length_ft,
                                    };
                                    setQuote((q) => ({ ...q, roof: { ...(q.roof || {}), measurements: refined } }));
                                    scheduleSave({ roof: { ...(quote.roof || {}), measurements: refined } });
                                    toast.success('EagleView measurements applied — re-running costs.');
                                    setTimeout(() => { applyAIToCosts?.(); }, 400);
                                }}
                            />
                        )}
                    */}

                    {/* Scope of Work — industry standard checkbox list */}
                    <ScopeOfWorkSection
                        scope={quote.scope_of_work || {}}
                        onChange={(s) => updateNested('scope_of_work', s)}
                        disabled={isLocked}
                    />

                    {/* Materials list — keeps the existing model, displayed as line items */}
                    <Section title="Materials" subtitle={`${(quote.materials || []).length} line items`} testId="section-materials">
                        <MaterialsTable
                            materials={quote.materials || []}
                            onChange={(arr) => { setQuote(q => ({ ...q, materials: arr })); scheduleSave({ materials: arr }); }}
                            disabled={isLocked}
                        />
                    </Section>

                    {/* Costs — itemized $ breakdown */}
                    <Section
                        title="Costs"
                        subtitle={`Subtotal ${$ (subtotal)} · Total ${$ (total)}`}
                        testId="section-costs"
                    >
                        <CostsBlock costs={c} onChange={(patch) => updateNested('costs', patch)} subtotal={subtotal} tax={tax} total={total} disabled={isLocked} />
                    </Section>

                    {/* Homeowner-facing range */}
                    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                        <CardContent className="p-4">
                            <div className="text-xs text-amber-800 font-semibold uppercase tracking-wider">Homeowner-facing range</div>
                            <div className="font-display text-2xl sm:text-3xl text-slate-900 mt-1 tabular-nums" data-testid="estimate-range-display">
                                {$ (rangeLow)} <span className="text-slate-400 mx-1">–</span> {$ (rangeHigh)}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                Shown to the customer on the PDF. Backed by an internal total of {$ (total)}.
                            </div>
                        </CardContent>
                    </Card>

                    {/* Warranties */}
                    <Section title="Warranties" defaultOpen={false} subtitle="Workmanship + manufacturer terms" testId="section-warranties">
                        <WarrantyBlock w={quote.warranties || {}} onChange={(p) => updateNested('warranties', p)} disabled={isLocked} />
                    </Section>

                    {/* Payment + timeline */}
                    <Section title="Payment terms & timeline" defaultOpen={false} testId="section-terms">
                        <TermsBlock
                            payment={quote.payment_terms || {}}
                            timeline={quote.timeline || {}}
                            onPayment={(p) => updateNested('payment_terms', p)}
                            onTimeline={(p) => updateNested('timeline', p)}
                            disabled={isLocked}
                        />
                    </Section>

                    {/* User-added custom sections */}
                    <CustomSections
                        sections={quote.custom_sections || []}
                        onChange={(arr) => updateField('custom_sections', arr)}
                        disabled={isLocked}
                    />

                    {/* Internal notes (not shown to homeowner) */}
                    <Section title="Internal notes" defaultOpen={false} subtitle="Private — never shown to the customer" testId="section-internal-notes">
                        <Textarea
                            value={quote.notes || ''}
                            onChange={(e) => updateField('notes', e.target.value)}
                            placeholder="Notes you want to keep on this job (not visible to the homeowner)…"
                            disabled={isLocked}
                            data-testid="field-internal-notes"
                            rows={4}
                        />
                    </Section>
                </div>

                {/* RIGHT: live PDF preview (desktop only) */}
                <aside className="hidden lg:block">
                    <div className="sticky top-20">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">PDF preview</div>
                            <span className="text-[10px] text-slate-400">Live — updates as you edit</span>
                        </div>
                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
                            <PdfPreviewPane
                                quote={quote}
                                subtotal={subtotal}
                                tax={tax}
                                total={total}
                                rangeLow={rangeLow}
                                rangeHigh={rangeHigh}
                            />
                        </div>
                    </div>
                </aside>
            </div>

            {/* ── Mobile-only floating "Preview PDF" button ─────────────── */}
            <div className="lg:hidden fixed bottom-4 right-4 z-30">
                <Button
                    onClick={() => setMobilePreview(true)}
                    className="rounded-full shadow-lg bg-slate-900 hover:bg-slate-800 text-white h-12 px-5"
                    data-testid="mobile-preview-button"
                >
                    <Eye className="h-4 w-4 mr-1.5" /> Preview PDF
                </Button>
            </div>

            {/* Mobile preview sheet */}
            <Sheet open={mobilePreview} onOpenChange={setMobilePreview}>
                <SheetContent side="bottom" className="h-[92vh] overflow-y-auto" data-testid="mobile-preview-sheet">
                    <SheetHeader>
                        <SheetTitle>PDF Preview</SheetTitle>
                    </SheetHeader>
                    <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden bg-white">
                        <PdfPreviewPane
                            quote={quote}
                            subtotal={subtotal}
                            tax={tax}
                            total={total}
                            rangeLow={rangeLow}
                            rangeHigh={rangeHigh}
                        />
                    </div>
                </SheetContent>
            </Sheet>

            {/* ── Map dialog ──────────────────────────────────────────── */}
            <Dialog open={mapDialog} onOpenChange={setMapDialog}>
                <DialogContent className="max-w-5xl" data-testid="map-dialog">
                    <DialogHeader><DialogTitle>Roof map — drag the pin onto the rooftop, then run AI Vision</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <RoofMapView
                            ref={roofMapRef}
                            address={quote.property_address}
                            initial={quote.roof}
                            onChange={() => { /* drawing handled by the underlying component */ }}
                            readOnly={isLocked}
                            height="55vh"
                        />
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="outline" onClick={() => setMapDialog(false)} data-testid="map-close-button">Close</Button>
                            <Button onClick={runAIEstimate} disabled={estimating || isLocked} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="map-run-ai">
                                {estimating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                                {estimating ? 'Measuring & pricing…' : 'Run AI Estimate (fills costs too)'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function StatBox({ label, value }) {
    return (
        <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-sm font-semibold text-slate-900 capitalize tabular-nums">{value}</div>
        </div>
    );
}


function CompactPreflight({ quality }) {
    if (!quality) return null;
    const tl = quality.traffic_light;
    if (tl === 'green') {
        return (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 font-medium" data-testid="preflight-pill">
                <CheckCircle2 className="h-3 w-3" /> Pre-flight passed — safe to send
            </div>
        );
    }
    const themes = {
        yellow: { Icon: AlertTriangle, color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-300' },
        red:    { Icon: XCircle,        color: 'text-rose-800',  bg: 'bg-rose-50',  border: 'border-rose-300' },
    };
    const t = themes[tl] || themes.yellow;
    const Icon = t.Icon;
    return (
        <div className={`rounded-md border ${t.border} ${t.bg} ${t.color} text-xs p-2`} data-testid="preflight-pill">
            <div className="flex items-center gap-2 font-medium">
                <Icon className="h-3.5 w-3.5" /> {quality.recommendation}
            </div>
        </div>
    );
}


function ScopeOfWorkSection({ scope, onChange, disabled }) {
    // We keep the underlying `scope_of_work` dict shape unchanged so the PDF
    // continues to render. Each industry-standard item becomes a checkbox that
    // toggles a boolean flag with the key matching SCOPE_DEFAULTS[i].key.
    const items = scope.items || {};
    const setItem = (key, val) => onChange({ items: { ...items, [key]: val } });
    const isChecked = (key) => (items[key] !== undefined ? !!items[key] : !!SCOPE_DEFAULTS.find(s => s.key === key)?.checked);

    return (
        <Section
            title="Scope of work"
            subtitle="Industry-standard line items — toggle off anything that doesn't apply"
            testId="section-scope"
        >
            <div className="space-y-1">
                {SCOPE_DEFAULTS.map((item) => (
                    <label
                        key={item.key}
                        className="flex items-start gap-3 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer"
                        data-testid={`scope-item-${item.key}`}
                    >
                        <Checkbox
                            checked={isChecked(item.key)}
                            onCheckedChange={(v) => setItem(item.key, !!v)}
                            disabled={disabled}
                            className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-900">{item.label}</div>
                            {item.hint && <div className="text-xs text-slate-500">{item.hint}</div>}
                        </div>
                    </label>
                ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
                <Label className="text-xs text-slate-600">Additional scope notes (optional)</Label>
                <Textarea
                    value={scope.notes || ''}
                    onChange={(e) => onChange({ notes: e.target.value })}
                    placeholder="e.g., Replace 2 skylights, paint new flashings to match…"
                    disabled={disabled}
                    rows={2}
                    data-testid="scope-notes"
                />
            </div>
        </Section>
    );
}


function MaterialsTable({ materials, onChange, disabled }) {
    const setRow = (i, patch) => {
        const arr = [...materials];
        arr[i] = { ...arr[i], ...patch };
        onChange(arr);
    };
    const removeRow = (i) => {
        const arr = [...materials];
        arr.splice(i, 1);
        onChange(arr);
    };
    const addRow = () => onChange([...materials, { category: 'Custom item', spec: '', quantity: '' }]);

    return (
        <div className="space-y-2">
            {materials.length === 0 && (
                <div className="text-sm text-slate-500 py-3">No materials yet — run AI Vision then click "Auto-fill costs" to populate the defaults.</div>
            )}
            {materials.map((m, i) => (
                <div
                    key={i}
                    className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_0.7fr_auto] gap-2 items-center p-2 rounded border border-slate-200 bg-slate-50/40"
                    data-testid={`material-row-${i}`}
                >
                    <Input
                        value={m.category || ''}
                        onChange={(e) => setRow(i, { category: e.target.value })}
                        placeholder="Category"
                        disabled={disabled}
                        className="text-sm"
                        data-testid={`material-${i}-category`}
                    />
                    <Input
                        value={m.spec || ''}
                        onChange={(e) => setRow(i, { spec: e.target.value })}
                        placeholder="Spec / product name"
                        disabled={disabled}
                        className="text-sm"
                        data-testid={`material-${i}-spec`}
                    />
                    <Input
                        value={m.quantity || ''}
                        onChange={(e) => setRow(i, { quantity: e.target.value })}
                        placeholder="Qty"
                        disabled={disabled}
                        className="text-sm"
                        data-testid={`material-${i}-qty`}
                    />
                    <Button
                        onClick={() => removeRow(i)}
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        className="text-rose-600 hover:text-rose-700"
                        data-testid={`material-${i}-remove`}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            ))}
            <Button onClick={addRow} variant="outline" size="sm" disabled={disabled} data-testid="material-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add material
            </Button>
        </div>
    );
}


function CostsBlock({ costs, onChange, subtotal, tax, total, disabled }) {
    const c = costs || {};
    const rows = [
        { key: 'materials', label: 'Materials' },
        { key: 'labor',     label: 'Labor' },
        { key: 'disposal',  label: 'Disposal / dump fee' },
        { key: 'permit',    label: 'Permit fee' },
        { key: 'other',     label: 'Overhead & profit' },
    ];
    return (
        <div className="space-y-2">
            {rows.map(r => (
                <div key={r.key} className="grid grid-cols-[1fr_140px] gap-2 items-center" data-testid={`cost-row-${r.key}`}>
                    <Label className="text-sm text-slate-700">{r.label}</Label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                        <Input
                            type="number"
                            min="0"
                            step="1"
                            value={c[r.key] ?? ''}
                            onChange={(e) => onChange({ [r.key]: Number(e.target.value) })}
                            disabled={disabled}
                            className="pl-7 text-right tabular-nums"
                            data-testid={`cost-${r.key}-input`}
                        />
                    </div>
                </div>
            ))}
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                <RowKv k="Subtotal" v={$2(subtotal)} />
                <div className="grid grid-cols-[1fr_140px] gap-2 items-center">
                    <Label className="text-sm text-slate-700">Tax rate</Label>
                    <div className="relative">
                        <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={c.tax_rate ?? ''}
                            onChange={(e) => onChange({ tax_rate: Number(e.target.value) })}
                            disabled={disabled}
                            className="pr-7 text-right tabular-nums"
                            data-testid="cost-tax-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                    </div>
                </div>
                <RowKv k="Tax" v={$2(tax)} />
                <RowKv k="Total" v={$2(total)} bold />
            </div>
        </div>
    );
}


function RowKv({ k, v, bold }) {
    return (
        <div className={`flex items-center justify-between text-sm ${bold ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
            <span>{k}</span>
            <span className="tabular-nums">{v}</span>
        </div>
    );
}


function WarrantyBlock({ w, onChange, disabled }) {
    return (
        <div className="grid sm:grid-cols-2 gap-3">
            <div>
                <Label className="text-xs text-slate-600">Workmanship warranty</Label>
                <Input
                    value={w.workmanship || ''}
                    onChange={(e) => onChange({ workmanship: e.target.value })}
                    placeholder="10-year workmanship warranty against installation defects"
                    disabled={disabled}
                    data-testid="warranty-workmanship"
                />
            </div>
            <div>
                <Label className="text-xs text-slate-600">Manufacturer warranty</Label>
                <Input
                    value={w.manufacturer || ''}
                    onChange={(e) => onChange({ manufacturer: e.target.value })}
                    placeholder="Per manufacturer terms — Limited Lifetime"
                    disabled={disabled}
                    data-testid="warranty-manufacturer"
                />
            </div>
        </div>
    );
}


function TermsBlock({ payment, timeline, onPayment, onTimeline, disabled }) {
    return (
        <div className="space-y-3">
            <div>
                <Label className="text-xs text-slate-600 font-semibold uppercase tracking-wider">Payment schedule</Label>
                <div className="grid sm:grid-cols-3 gap-2 mt-1">
                    <Field label="Deposit" value={payment.deposit} onChange={(v) => onPayment({ deposit: v })} placeholder="30% on signing" testId="terms-deposit" disabled={disabled} />
                    <Field label="Progress" value={payment.progress} onChange={(v) => onPayment({ progress: v })} placeholder="40% after tear-off" testId="terms-progress" disabled={disabled} />
                    <Field label="Final" value={payment.final} onChange={(v) => onPayment({ final: v })} placeholder="30% on completion" testId="terms-final" disabled={disabled} />
                </div>
                <Field label="Accepted methods" value={payment.methods} onChange={(v) => onPayment({ methods: v })} placeholder="Check, ACH, Credit Card (3% fee)" testId="terms-methods" disabled={disabled} className="mt-2" />
            </div>
            <div>
                <Label className="text-xs text-slate-600 font-semibold uppercase tracking-wider">Timeline</Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-1">
                    <Field label="Start date" value={timeline.start_date} onChange={(v) => onTimeline({ start_date: v })} placeholder="Within 14 days of signing" testId="timeline-start" disabled={disabled} />
                    <Field label="Duration" value={timeline.duration} onChange={(v) => onTimeline({ duration: v })} placeholder="1–2 days" testId="timeline-duration" disabled={disabled} />
                </div>
            </div>
        </div>
    );
}


function CustomSections({ sections, onChange, disabled }) {
    const addSection = () => {
        const next = [
            ...sections,
            { id: `cs_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, title: '', body: '', order: sections.length + 1 },
        ];
        onChange(next);
    };
    const updateSec = (idx, patch) => {
        const next = [...sections];
        next[idx] = { ...next[idx], ...patch };
        onChange(next);
    };
    const removeSec = (idx) => {
        const next = [...sections];
        next.splice(idx, 1);
        onChange(next);
    };

    return (
        <div className="space-y-2" data-testid="custom-sections-wrapper">
            {sections.map((sec, idx) => (
                <Section
                    key={sec.id || idx}
                    title={sec.title || 'Custom section'}
                    subtitle="Appears in the PDF after Warranties"
                    defaultOpen={true}
                    testId={`custom-section-${idx}`}
                    action={
                        <Button
                            onClick={() => removeSec(idx)}
                            variant="ghost"
                            size="sm"
                            disabled={disabled}
                            className="text-rose-600 hover:text-rose-700"
                            data-testid={`custom-section-${idx}-remove`}
                        >
                            <X className="h-3.5 w-3.5" />
                        </Button>
                    }
                >
                    <div className="space-y-2">
                        <Field
                            label="Section title"
                            value={sec.title}
                            onChange={(v) => updateSec(idx, { title: v })}
                            placeholder="e.g., HOA Requirements, Color Selection, Insurance Claim Notes"
                            disabled={disabled}
                            testId={`custom-section-${idx}-title`}
                        />
                        <div>
                            <Label className="text-xs text-slate-600">Section body</Label>
                            <Textarea
                                value={sec.body || ''}
                                onChange={(e) => updateSec(idx, { body: e.target.value })}
                                placeholder="Add anything that doesn't fit the standard sections…"
                                disabled={disabled}
                                rows={4}
                                data-testid={`custom-section-${idx}-body`}
                            />
                        </div>
                    </div>
                </Section>
            ))}
            <Button
                onClick={addSection}
                variant="outline"
                size="sm"
                disabled={disabled}
                data-testid="custom-section-add"
                className="w-full sm:w-auto"
            >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add custom section
            </Button>
        </div>
    );
}
